import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';

interface SensitiveCellProps {
  secretMasked: string | null;
  secretEncrypted: boolean;
  /** C044 시연: 토글로 평문 표시. MVP에서는 secretMasked 를 그대로 노출 */
  rowId: number;
}

export function SensitiveCell({ secretMasked, secretEncrypted, rowId }: SensitiveCellProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);

  const toggle = useCallback(() => {
    setRevealed((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  if (!secretMasked && !secretEncrypted) {
    return (
      <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
        {t('tenantSwitcher.samples.noSecret')}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
      {/* 암호화 배지 */}
      {secretEncrypted && (
        <StatusPill tone="info">
          <span style={{ fontSize: 10 }}>🔒</span> {t('tenantSwitcher.sensitive.encryptedBadge')}
        </StatusPill>
      )}

      {/* 값 표시 */}
      <span
        style={{
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          color: revealed ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        }}
        aria-live="polite"
        aria-label={`행 ${rowId} 민감값`}
      >
        {secretMasked ?? '—'}
      </span>

      {/* 토글 버튼 (C044 복호화 시연) */}
      {secretEncrypted && secretMasked && (
        <button
          type="button"
          onClick={toggle}
          onKeyDown={handleKeyDown}
          aria-label={
            revealed
              ? t('tenantSwitcher.sensitive.toggleMask')
              : t('tenantSwitcher.sensitive.toggleReveal')
          }
          title={
            revealed
              ? t('tenantSwitcher.sensitive.toggleMask')
              : t('tenantSwitcher.sensitive.toggleReveal')
          }
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px var(--spacing-1)',
            borderRadius: 'var(--radius-sm)',
            color: revealed ? 'var(--color-brand-primary)' : 'var(--color-text-muted)',
            fontSize: 'var(--font-size-base)',
            lineHeight: 1,
            fontFamily: 'var(--font-family-sans)',
            transition: 'color 0.15s',
          }}
        >
          {revealed ? '🙈' : '👁'}
        </button>
      )}
    </div>
  );
}
